import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  CheckCircle2, XCircle, Loader2, Zap, Receipt, Settings, Play, Eye, AlertTriangle, ChevronRight, RotateCcw,
} from 'lucide-react'
import {
  getXeroSettings, updateXeroSettings, runBilling, disconnectXero, getXeroConnectUrl, getAdminClients,
} from '../../lib/api'
import { useToast, ToastContainer } from '../../components/Toast'

function formatDueDate(v) {
  if (!v) return ''
  const d = new Date(`${v}T00:00:00`)
  return Number.isNaN(d.getTime())
    ? String(v)
    : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatCurrency(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(v ?? 0)
}

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]

// ── Preview Modal ────────────────────────────────────────────────────────────

function PreviewModal({ year, month, xeroConnected, force, onClose, onConfirm, isRunning }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['billing-preview', year, month, force],
    queryFn: () => runBilling(year, month, true, force),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  })

  const { data: adminClients } = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: getAdminClients,
    // MTD here is the reconciliation check against a freshly-computed invoice,
    // so it must be fresh too. This key is shared with the Dashboard and the
    // Clients page, and with a 60s staleTime the preview reused whatever they
    // had already fetched — showing ANGEL BOY at $544.72 beside an invoice of
    // $583.90, when a live read of the same endpoint gives $583.90. The figures
    // never actually disagreed; one of them was just minutes old.
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const preview = data?.preview ?? []
  const billable = preview.filter(c => c.grand_total > 0 && !c.would_skip)
  const grandTotal = billable.reduce((s, c) => s + c.grand_total, 0)

  // Build MTD lookup by client name for reconciliation
  const mtdByName = {}
  const mtdIncompleteByName = {}
  if (adminClients) {
    for (const c of adminClients) {
      mtdByName[c.name] = c.mtd_total
      // Non-empty => at least one MTD bucket's query failed and contributed $0,
      // so this figure UNDERSTATES. Without surfacing it, a failed bucket looks
      // exactly like a genuine zero and the reconciliation badge reads red for
      // a reason nobody can see.
      mtdIncompleteByName[c.name] = c.mtd_incomplete || null
    }
  }
  const mtdTotal = billable.reduce((s, c) => s + (mtdByName[c.client_name] ?? 0), 0)
  const reconcileDiff = grandTotal - mtdTotal
  const reconciled = Math.abs(reconcileDiff) < 0.10

  // Per-client selection — which clients this run will actually invoice.
  const [selected, setSelected] = useState(() => new Set())
  const billableIds      = billable.map(c => c.client_id).filter(Boolean)
  const selectedBillable = billable.filter(c => selected.has(c.client_id))
  const selectedTotal    = selectedBillable.reduce((s, c) => s + c.grand_total, 0)

  // Seed / refresh the selection from the billable set, keyed on its identity so
  // a background refetch can't silently under-bill a newly-billable client.
  //  • First load with Force Re-run ON  → start EMPTY: the admin must explicitly
  //    tick the client(s) to regenerate (NOT default to re-billing everyone —
  //    that's the whole point of the tick boxes).
  //  • First load without force         → default to all billable (normal run).
  //  • Later refetch                    → keep the admin's toggles, drop ids no
  //    longer billable, and (non-force only) add any newly-billable ids.
  const billableKey = billableIds.join(',')
  const seenKeyRef = useRef('')
  useEffect(() => {
    if (isLoading || billableKey === seenKeyRef.current) return
    const firstSeed = seenKeyRef.current === ''
    seenKeyRef.current = billableKey
    setSelected(prev => {
      if (firstSeed) return force ? new Set() : new Set(billableIds)
      const next = new Set([...prev].filter(id => billableIds.includes(id)))
      if (!force) billableIds.forEach(id => next.add(id))
      return next
    })
  }, [isLoading, billableKey, force])   // eslint-disable-line react-hooks/exhaustive-deps

  const toggleClient = (id) => setSelected(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              Billing Preview — {MONTHS[month - 1]} {year}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Review what will be invoiced before committing.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          >
            <XCircle size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {isLoading && (
            <div className="flex justify-center py-10">
              <Loader2 size={24} className="animate-spin text-slate-400" />
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              Failed to generate preview: {error.message}
            </div>
          )}

          {!isLoading && !error && (
            <>
              {!xeroConnected && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>
                    Xero is not connected. Invoices will be saved to the database only — no Xero invoice will be created.
                  </span>
                </div>
              )}

              {data?.warnings?.length > 0 && data.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{w}</span>
                </div>
              ))}

              {force && preview.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg bg-orange-50 border border-orange-200 px-4 py-2 text-xs text-orange-700">
                  <RotateCcw size={14} className="flex-shrink-0 mt-0.5" />
                  <span>Force Re-run is on — each <strong>ticked</strong> client's existing invoice will be deleted in Xero and recreated. Only the clients you tick are affected.</span>
                </div>
              )}
              {preview.length > 0 && (
                <div className="flex items-center justify-between text-xs px-1 pb-1">
                  <span className="text-slate-500">
                    <span className="font-medium text-slate-700">{selectedBillable.length}</span> of {billable.length} selected to invoice
                  </span>
                  <div className="flex gap-3">
                    <button onClick={() => setSelected(new Set(billableIds))} className="text-blue-600 hover:underline">Select all</button>
                    <button onClick={() => setSelected(new Set())} className="text-slate-500 hover:underline">Deselect all</button>
                  </div>
                </div>
              )}
              {preview.length === 0 ? (
                <p className="text-center text-slate-400 py-8 text-sm">No clients to bill this period.</p>
              ) : (
                preview.map((client) => (
                  <div
                    key={client.client_name}
                    className={`rounded-xl border p-4 ${
                      client.would_skip || client.grand_total === 0
                        ? 'border-slate-200 bg-slate-50 opacity-60'
                        : client.existing_invoice && !client.would_skip
                          ? 'border-orange-200 bg-orange-50/40'
                          : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:opacity-40 disabled:cursor-default"
                          checked={selected.has(client.client_id)}
                          disabled={client.would_skip || client.grand_total === 0 || !client.client_id}
                          onChange={() => toggleClient(client.client_id)}
                          title={
                            client.would_skip
                              ? 'Already invoiced — enable Force Re-run to regenerate'
                              : client.grand_total === 0
                                ? 'Nothing to invoice'
                                : 'Include this client in the run'
                          }
                        />
                        <span className="font-semibold text-slate-800 text-sm">{client.client_name}</span>
                        {client.would_skip && (
                          <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                            already invoiced
                          </span>
                        )}
                        {client.existing_invoice && !client.would_skip && (
                          <span className="text-xs bg-orange-100 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <RotateCcw size={10} /> re-run
                          </span>
                        )}
                        {client.order_count === 0 && client.grand_total === 0 && !client.would_skip && (
                          <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                            no charges
                          </span>
                        )}
                        {client.order_count === 0 && client.grand_total > 0 && !client.would_skip && (
                          <span className="text-xs bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 rounded-full">
                            storage only
                          </span>
                        )}
                        {!client.has_xero_contact && client.grand_total > 0 && !client.would_skip && (
                          <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                            no Xero contact
                          </span>
                        )}
                        {/* The due date this invoice will actually carry.
                            Until Oct 2026 the preview showed no due date at
                            all, which is how seven invoices went out on a
                            fallback date nobody had chosen. Amber when the
                            portal has no terms for the client, because that
                            is exactly the case where the date is invented. */}
                        {client.due_date && client.grand_total > 0 && !client.would_skip && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full border ${
                              client.payment_terms
                                ? 'bg-slate-50 text-slate-600 border-slate-200'
                                : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`}
                            title={
                              client.payment_terms
                                ? `Xero payment terms: ${client.payment_terms}`
                                : 'No payment terms known for this client — this is the fallback due date, not their Xero terms.'
                            }
                          >
                            due {formatDueDate(client.due_date)}
                            {!client.payment_terms && ' (default)'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {(() => {
                          const mtd = mtdByName[client.client_name]
                          if (mtd == null) return null
                          const incomplete = mtdIncompleteByName[client.client_name]
                          const diff = client.grand_total - mtd
                          const match = Math.abs(diff) < 0.10
                          // An incomplete MTD is not a mismatch — it is a
                          // figure we could not finish computing. Saying
                          // "doesn't reconcile" would send you hunting for a
                          // billing bug that isn't there.
                          if (incomplete) {
                            return (
                              <span
                                className="text-xs font-mono px-2 py-0.5 rounded-full border bg-slate-100 text-slate-500 border-slate-300"
                                title={`MTD could not be fully computed — ${incomplete.join(', ')} failed to load. `
                                     + `This figure understates and cannot be reconciled against the invoice. `
                                     + `Reload to retry; check the server log if it persists.`}
                              >
                                MTD unavailable
                              </span>
                            )
                          }
                          return (
                            <span className={`text-xs font-mono px-2 py-0.5 rounded-full border ${
                              match
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : 'bg-red-50 text-red-600 border-red-200'
                            }`}>
                              MTD {formatCurrency(mtd)}
                              {!match && <span className="ml-1">({diff > 0 ? '+' : ''}{formatCurrency(diff)})</span>}
                            </span>
                          )
                        })()}
                        <span className="font-bold text-slate-800 text-sm">{formatCurrency(client.grand_total)}</span>
                      </div>
                    </div>

                    {client.line_items.length > 0 && (() => {
                      const rolloverTotal = client.line_items.reduce((s, li) => s + (li.rollover || 0), 0)
                      return (
                        <table className="w-full text-xs">
                          <tbody className="divide-y divide-slate-100">
                            {client.line_items.map((li, i) => (
                              <tr key={i}>
                                <td className="py-1 text-slate-600">{li.description}</td>
                                <td className="py-1 text-slate-400 font-mono w-20">
                                  {li.itemCode && <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{li.itemCode}</span>}
                                </td>
                                <td className="py-1 text-right text-slate-500 w-16">{li.accountCode}</td>
                                <td className="py-1 text-right font-medium text-slate-700 w-24">
                                  {li.quantity} × {formatCurrency(li.unitAmount)}
                                </td>
                                <td
                                  className="py-1 text-right font-medium w-28 text-purple-700"
                                  title={li.rollover > 0 ? 'Unbilled amount from prior periods swept into this invoice' : ''}
                                >
                                  {li.rollover > 0 ? `+${formatCurrency(li.rollover)}` : <span className="text-slate-300">—</span>}
                                </td>
                              </tr>
                            ))}
                            {rolloverTotal > 0 && (
                              <tr className="border-t border-slate-200">
                                <td colSpan={4} className="py-1 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                  Rollover total
                                </td>
                                <td className="py-1 text-right font-bold text-purple-700 w-28">
                                  +{formatCurrency(rolloverTotal)}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )
                    })()}
                  </div>
                ))
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200">
          {/* Reconciliation row */}
          {adminClients && billable.length > 0 && (
            <div className={`flex items-center justify-between px-6 py-3 text-xs border-b ${
              reconciled ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'
            }`}>
              <div className={`flex items-center gap-2 font-medium ${reconciled ? 'text-green-700' : 'text-red-700'}`}>
                {reconciled ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
                <span>vs. MTD Admin Overview: {formatCurrency(mtdTotal)}</span>
              </div>
              <div className={`font-mono font-semibold ${reconciled ? 'text-green-700' : 'text-red-700'}`}>
                {reconciled
                  ? 'Balanced ✓'
                  : `${reconcileDiff > 0 ? '+' : ''}${formatCurrency(reconcileDiff)}`
                }
              </div>
            </div>
          )}

          <div className="flex items-center justify-between px-6 py-4">
            <div className="text-sm text-slate-600">
              <span className="font-semibold">{selectedBillable.length}</span> client{selectedBillable.length !== 1 ? 's' : ''} to invoice
              {selectedTotal > 0 && (
                <span className="ml-2 font-bold text-slate-800">{formatCurrency(selectedTotal)}</span>
              )}
              {selectedBillable.filter(c => c.existing_invoice).length > 0 && (
                <span className="ml-2 text-xs text-orange-600">
                  ({selectedBillable.filter(c => c.existing_invoice).length} re-run)
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-secondary">Cancel</button>
              <button
                onClick={() => onConfirm(selectedBillable.map(c => c.client_id))}
                disabled={isRunning || isLoading || selectedBillable.length === 0}
                className="btn-primary"
              >
                {isRunning ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                Run Billing
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function AdminBilling() {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const [search, setSearch] = useSearchParams()

  // Back from the Xero callback (backend/routers/auth.py), which never 4xxs:
  // ?xero=connected or ?xero_error=<message>. Toast once, then strip them.
  useEffect(() => {
    const err = search.get('xero_error')
    const ok = search.get('xero') === 'connected'
    if (!err && !ok) return
    if (err) addToast({ type: 'error', message: `Xero connect didn't finish: ${err}`, duration: 10000 })
    else {
      addToast({ type: 'success', message: 'Xero connected.' })
      queryClient.invalidateQueries({ queryKey: ['xero-settings'] })
    }
    const next = new URLSearchParams(search)
    next.delete('xero')
    next.delete('xero_error')
    setSearch(next, { replace: true })
  }, [search, setSearch, addToast, queryClient])

  const now = new Date()
  const [year, setYear]     = useState(now.getFullYear())
  const [month, setMonth]   = useState(now.getMonth() + 1)
  const [showPreview, setShowPreview] = useState(false)
  const [force, setForce]   = useState(false)
  const [testMode, setTestMode] = useState(false)

  // Settings form state — null until data loads
  const [codes, setCodes] = useState(null)

  const { data: xeroSettings, isLoading: settingsLoading } = useQuery({
    queryKey: ['xero-settings'],
    queryFn: getXeroSettings,
  })

  // Initialise form from server data once
  if (xeroSettings && codes === null) {
    setCodes({
      item_code_receiving:                 xeroSettings.item_code_receiving                 ?? '',
      item_code_storage_ambient_pallet:    xeroSettings.item_code_storage_ambient_pallet    ?? '',
      item_code_storage_ambient_bin:       xeroSettings.item_code_storage_ambient_bin       ?? '',
      item_code_storage_controlled_pallet: xeroSettings.item_code_storage_controlled_pallet ?? '',
      item_code_storage_controlled_bin:    xeroSettings.item_code_storage_controlled_bin    ?? '',
      item_code_fulfilment:                xeroSettings.item_code_fulfilment                ?? '',
      item_code_case_pick:                 xeroSettings.item_code_case_pick                 ?? '',
      item_code_returns:                   xeroSettings.item_code_returns                   ?? '',
      item_code_return_processing:         xeroSettings.item_code_return_processing         ?? '',
      item_code_return_additional_items:   xeroSettings.item_code_return_additional_items   ?? '',
      item_code_additional_items:          xeroSettings.item_code_additional_items          ?? '',
      item_code_order_details:             xeroSettings.item_code_order_details             ?? '',
      item_code_labelling:                 xeroSettings.item_code_labelling                 ?? '',
      item_code_inserts:                   xeroSettings.item_code_inserts                   ?? '',
      item_code_customs_inserts:           xeroSettings.item_code_customs_inserts           ?? '',
      item_code_time:                      xeroSettings.item_code_time                      ?? '',
      item_code_shipping:                  xeroSettings.item_code_shipping                  ?? '',
      item_code_discrepancies:             xeroSettings.item_code_discrepancies             ?? '',
    })
  }

  const saveCodesMutation = useMutation({
    mutationFn: updateXeroSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['xero-settings'] })
      addToast({ type: 'success', message: 'Item codes saved.' })
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Save failed.' }),
  })

  const disconnectMutation = useMutation({
    mutationFn: disconnectXero,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['xero-settings'] })
      addToast({ type: 'success', message: 'Xero disconnected.' })
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Disconnect failed.' }),
  })


  const runMutation = useMutation({
    mutationFn: (clientIds = null) => runBilling(year, month, false, force, testMode, clientIds),
    onSuccess: (result) => {
      setShowPreview(false)
      queryClient.invalidateQueries({ queryKey: ['billing-preview'] })
      const msg = result.errors?.length > 0
        ? `Done with errors: ${result.errors.join('; ')}`
        : `Billing complete — ${result.invoices_created} invoice${result.invoices_created !== 1 ? 's' : ''} created, ${result.invoices_skipped} skipped.`
      addToast({
        type: result.errors?.length > 0 ? 'error' : 'success',
        message: msg,
      })
    },
    onError: (err) => addToast({ type: 'error', message: err.response?.data?.detail || 'Billing run failed.' }),
  })

  const connected = xeroSettings?.connected ?? false

  // Build year options — current year ± 2
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i)

  return (
    <div className="space-y-6 p-6">
      {/* Page title */}
      <div>
        <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Receipt size={22} className="text-blue-600" />
          Billing
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage Xero integration and run month-end billing.</p>
      </div>

      {/* ── Section A: Xero Connection ────────────────────────────────── */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Zap size={16} className="text-slate-500" />
          <h2 className="text-sm font-semibold text-slate-700">Xero Connection</h2>
        </div>

        {settingsLoading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <Loader2 size={14} className="animate-spin" /> Checking connection…
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {connected ? (
                <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 border border-green-200 text-xs font-semibold px-3 py-1.5 rounded-full">
                  <CheckCircle2 size={12} /> Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 text-xs font-semibold px-3 py-1.5 rounded-full">
                  <XCircle size={12} /> Not Connected
                </span>
              )}
              {connected && xeroSettings?.tenant_id && (
                <span className="text-xs text-slate-400 font-mono">
                  Tenant: {xeroSettings.tenant_id}
                </span>
              )}
            </div>

            <div className="flex gap-2">
              {connected ? (
                <button
                  onClick={() => disconnectMutation.mutate()}
                  disabled={disconnectMutation.isPending}
                  className="btn-secondary text-xs py-1.5 px-3 text-red-600 hover:border-red-300 hover:bg-red-50"
                >
                  {disconnectMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={async () => {
                    try {
                      const url = await getXeroConnectUrl()
                      window.location.href = url
                    } catch (e) {
                      addToast({ type: 'error', message: 'Could not get Xero connect URL.' })
                    }
                  }}
                  className="btn-primary text-xs py-1.5 px-3"
                >
                  <Zap size={12} /> Connect to Xero
                </button>
              )}
            </div>
          </div>
        )}

        {!connected && !settingsLoading && (
          <p className="mt-3 text-xs text-slate-400">
            Connect Xero to automatically create invoices during billing runs. Without a connection, invoices are saved to the database only.
          </p>
        )}
      </div>

      {/* ── Section B: Xero Settings ──────────────────────────────────── */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-1">
          <Settings size={16} className="text-slate-500" />
          <h2 className="text-sm font-semibold text-slate-700">Xero Item Codes</h2>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          Set once — these codes apply to every billing run going forward and persist between months. Fields marked <span className="text-red-500 font-bold">*</span> appear on every invoice; if left blank those line items will have no item code in Xero. Blank fields are ignored on save, so accidentally clearing a field won't overwrite the stored value.
        </p>

        {(() => {
          const groups = [
            { group: 'Stock Receiving',    items: [
              { key: 'item_code_receiving', label: 'Goods & Packages Received' },
            ]},
            { group: 'Storage',            items: [
              { key: 'item_code_storage_ambient_pallet',    label: 'Ambient — Pallets',              required: true },
              { key: 'item_code_storage_ambient_bin',       label: 'Ambient — Bins/Shelves' },
              { key: 'item_code_storage_controlled_pallet', label: 'Cold/Controlled — Pallets' },
              { key: 'item_code_storage_controlled_bin',    label: 'Cold/Controlled — Bins/Shelves' },
            ]},
            { group: 'Fulfilment',         items: [
              { key: 'item_code_fulfilment',       label: 'Orders Fulfilled',            required: true },
              { key: 'item_code_case_pick',        label: 'Case Picks',                  required: true },
              { key: 'item_code_additional_items', label: 'Additional Items',            required: true },
              { key: 'item_code_order_details',    label: 'Order Details (SKU charges)', required: true },
              { key: 'item_code_labelling',        label: 'Labelling',                   required: true },
              { key: 'item_code_inserts',          label: 'Inserts',                     required: true },
              { key: 'item_code_customs_inserts',  label: 'Customs Inserts' },
              { key: 'item_code_time',             label: 'Time / Labour',               required: true },
            ]},
            { group: 'Shipping',           items: [
              { key: 'item_code_shipping',      label: 'Courier, Exports & Tariffs',           required: true },
              { key: 'item_code_discrepancies', label: 'Postage Discrepancies / Refunds',      required: true },
              { key: 'item_code_returns',       label: 'Return Labels (courier cost)',         required: true },
            ]},
            { group: 'Returns Processing', items: [
              { key: 'item_code_return_processing',       label: 'Return Processing',           required: true },
              { key: 'item_code_return_additional_items', label: 'Additional Items — Returns',  required: true },
            ]},
          ]
          const groupAccent = {
            'Stock Receiving':    'text-slate-600',
            'Storage':            'text-blue-700',
            'Fulfilment':         'text-emerald-700',
            'Shipping':           'text-violet-700',
            'Returns Processing': 'text-amber-700',
          }
          return (
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    <th className="px-3 py-2 w-40">Category</th>
                    <th className="px-3 py-2">Item</th>
                    <th className="px-3 py-2 w-56">Xero Item Code</th>
                    <th className="px-3 py-2 w-32 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {groups.flatMap(({ group, items }) =>
                    items.map((item, idx) => {
                      const value = codes?.[item.key] ?? ''
                      const blank = item.required && codes !== null && !value
                      const filled = !!value
                      return (
                        <tr key={item.key} className="hover:bg-slate-50/60">
                          {idx === 0 ? (
                            <td
                              rowSpan={items.length}
                              className={`px-3 py-2 align-top text-xs font-semibold border-r border-slate-100 bg-slate-50/40 ${groupAccent[group] || 'text-slate-600'}`}
                            >
                              {group}
                            </td>
                          ) : null}
                          <td className="px-3 py-2 text-slate-700">
                            {item.label}
                            {item.required && <span className="text-red-500 ml-1">*</span>}
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={value}
                              onChange={(e) => setCodes((prev) => ({ ...prev, [item.key]: e.target.value }))}
                              className={`input font-mono text-sm w-full ${blank ? 'border-amber-400 bg-amber-50 placeholder:text-amber-400' : ''}`}
                              placeholder="e.g. 1002.1"
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            {filled ? (
                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                                <CheckCircle2 size={11} /> Set
                              </span>
                            ) : item.required ? (
                              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700" title="Required — will be missing on invoices">
                                <AlertTriangle size={11} /> Missing
                              </span>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          )
        })()}

        <div className="mt-4 flex justify-end">
          <button
            onClick={() => saveCodesMutation.mutate(codes)}
            disabled={saveCodesMutation.isPending || !codes}
            className="btn-primary text-xs py-1.5 px-4"
          >
            {saveCodesMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : null}
            Save Item Codes
          </button>
        </div>
      </div>

      {/* ── Section C: Billing Run ────────────────────────────────────── */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-1">
          <Play size={16} className="text-slate-500" />
          <h2 className="text-sm font-semibold text-slate-700">Run Billing</h2>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          Preview then confirm a billing run for a specific month. Existing invoices are skipped automatically.
        </p>

        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {MONTHS.map((name, i) => (
              <option key={i + 1} value={i + 1}>{name}</option>
            ))}
          </select>

          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>

          <button
            onClick={() => setShowPreview(true)}
            className="btn-secondary text-sm"
          >
            <Eye size={14} /> Preview Billing
          </button>

          <span className="text-slate-300 select-none">or</span>

          <button
            onClick={() => runMutation.mutate()}
            disabled={runMutation.isPending}
            className="btn-primary text-sm"
          >
            {runMutation.isPending
              ? <Loader2 size={14} className="animate-spin" />
              : <ChevronRight size={14} />
            }
            Run Now
          </button>
        </div>

        {/* Test mode toggle */}
        <div className={`mt-4 flex items-start gap-3 rounded-lg border px-4 py-3 ${
          testMode ? 'border-purple-300 bg-purple-50' : 'border-slate-200 bg-slate-50'
        }`}>
          <button
            type="button"
            role="switch"
            aria-checked={testMode}
            onClick={() => setTestMode(t => !t)}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-1 mt-0.5 ${
              testMode ? 'bg-purple-500' : 'bg-slate-300'
            }`}
          >
            <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              testMode ? 'translate-x-4' : 'translate-x-0'
            }`} />
          </button>
          <div>
            <p className={`text-sm font-medium ${testMode ? 'text-purple-800' : 'text-slate-700'}`}>
              <AlertTriangle size={13} className="inline mr-1.5 mb-0.5" />
              Test Mode (push to Xero, don't lock the period)
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Creates Xero invoices for every client (with workbook attachments) but skips all local DB writes — no <code className="text-[11px] bg-white px-1 rounded border border-slate-200">invoices</code> rows, no <code className="text-[11px] bg-white px-1 rounded border border-slate-200">orders.invoice_id</code> linking, period stays unlocked. Use to verify Xero connectivity, layout, attachments, due dates. <span className="font-semibold text-purple-700">Delete the test invoices from Xero when done.</span>
            </p>
          </div>
        </div>

        {/* Force re-run toggle */}
        <div className={`mt-4 flex items-start gap-3 rounded-lg border px-4 py-3 ${
          force ? 'border-orange-300 bg-orange-50' : 'border-slate-200 bg-slate-50'
        }`}>
          <button
            type="button"
            role="switch"
            aria-checked={force}
            onClick={() => setForce(f => !f)}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-1 mt-0.5 ${
              force ? 'bg-orange-500' : 'bg-slate-300'
            }`}
          >
            <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              force ? 'translate-x-4' : 'translate-x-0'
            }`} />
          </button>
          <div>
            <p className={`text-sm font-medium ${force ? 'text-orange-800' : 'text-slate-700'}`}>
              <RotateCcw size={13} className="inline mr-1.5 mb-0.5" />
              Force Re-run (re-issue a LIVE invoice)
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Deleted, voided and draft invoices regenerate <strong>automatically</strong>. Use Force only to re-issue an invoice that's still live in Xero — Force now <strong>voids the old Xero invoice</strong> and creates a fresh one, so there's no duplicate and no manual cleanup. (A <strong>PAID</strong> invoice can't be voided — that client is skipped with an error for you to handle.)
            </p>
          </div>
        </div>

        {runMutation.isSuccess && !runMutation.isPending && (
          <div className="mt-4 space-y-2">
            <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
              <span className="font-semibold">{runMutation.data.invoices_created}</span> invoice{runMutation.data.invoices_created !== 1 ? 's' : ''} created,{' '}
              <span className="font-semibold">{runMutation.data.invoices_skipped}</span> skipped.
              {runMutation.data.errors?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {runMutation.data.errors.map((e, i) => (
                    <li key={i} className="flex items-start gap-1 text-red-600 text-xs">
                      <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />{e}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {runMutation.data.warnings?.map((w, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showPreview && (
        <PreviewModal
          year={year}
          month={month}
          xeroConnected={connected}
          force={force}
          onClose={() => setShowPreview(false)}
          onConfirm={(ids) => runMutation.mutate(ids)}
          isRunning={runMutation.isPending}
        />
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}
